import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../../store/store';
import { updateSessionFields } from '../../../store/sessionSlice';
import { TranscriptSegment } from '../../../store/transcriptSlice';
import { showError } from '../../../store/toastsSlice';
import { cleanupRecording } from '../../../utils/recordingCleanup';
import { sessionService } from '../../../services/sessionService';
import { websocketService } from '../../../services/websocketService';
import { ConsentStatus } from './consentStatus';
import { MODAL_KEYS } from '../../modal/modalKeys';
import { useModal } from '../../modal/useModal';

interface UseConsentGateParams {
  /** When false (e.g. prod), all consent-gate side effects are disabled. */
  enabled: boolean;
  sessionId: string | undefined;
  consentStatus: ConsentStatus;
  isRecording: boolean;
  isFileStreaming: boolean;
  segments: TranscriptSegment[];
}

/**
 * Consent-decision orchestration for the session page. Opens the confirmation modal
 * for decisions that arise during THIS session — a fresh decline, or a recording that
 * ends still pending — and stays quiet for a session merely opened already
 * decided/unanswered in the past. Returns `openConsentModal` for on-demand use (the
 * banner's Resolve button).
 */
export function useConsentGate({
  enabled,
  sessionId,
  consentStatus,
  isRecording,
  isFileStreaming,
  segments,
}: UseConsentGateParams): { openConsentModal: () => void } {
  const dispatch = useAppDispatch();
  const { openModal, closeModal } = useModal();

  // The modal renders at the app root, so it would outlive this page. Close it on the way
  // out: its handlers belong to this session, and confirming a decline on another page would
  // still erase this session's transcript.
  useEffect(() => closeModal, [closeModal]);

  const initialConsentRef = useRef<ConsentStatus | null>(null);
  const capturedSessionRef = useRef<string | undefined>(undefined);
  const declineHandledRef = useRef(false);
  const finishPromptHandledRef = useRef(false);
  const wasActivelyTranscribingRef = useRef(false);
  const activeTranscriptIdRef = useRef<string | null>(null);
  const transcriptIdSessionRef = useRef<string | undefined>(undefined);
  const pendingDeleteRef = useRef(false);

  // Delete-id source: the live recording's transcript id while it runs, otherwise the
  // newest persisted segment — so declining a reopened session still erases its transcript.
  useEffect(() => {
    if (!enabled) return;
    if (transcriptIdSessionRef.current !== sessionId) {
      transcriptIdSessionRef.current = sessionId;
      activeTranscriptIdRef.current = null;
      pendingDeleteRef.current = false;
    }
    const liveTid = websocketService.getTranscriptId();
    if (liveTid) {
      activeTranscriptIdRef.current = liveTid;
    } else {
      for (let i = segments.length - 1; i >= 0; i--) {
        const t = segments[i].transcript_id;
        if (t && t !== 'live') {
          activeTranscriptIdRef.current = t;
          break;
        }
      }
    }
    // A decline was confirmed before the transcript id was known; erase it now it is. Rare scenario.
    if (pendingDeleteRef.current && sessionId && activeTranscriptIdRef.current) {
      pendingDeleteRef.current = false;
      sessionService.deleteTranscript(sessionId, activeTranscriptIdRef.current).catch(() => {
        dispatch(showError('Failed to delete consent-window transcript'));
      });
    }
  }, [enabled, sessionId, segments, isRecording, isFileStreaming, dispatch]);

  const openConsentModal = useCallback(() => {
    openModal(MODAL_KEYS.CONSENT_DECLINE, {
      onConfirmDecline: () => {
        // Settles both prompts: the decision is made, so neither a fresh decline nor a
        // recording that ends still pending should ask again. The finish guard matters even
        // here — the consent write is an async thunk while cleanupRecording stops the
        // recording synchronously, so the next render still reads 'pending' with recording
        // just stopped, which is exactly the finish-prompt trigger.
        declineHandledRef.current = true;
        finishPromptHandledRef.current = true;
        // Capture before cleanupRecording, which disconnects the socket and nulls the live id.
        const transcriptId = activeTranscriptIdRef.current || websocketService.getTranscriptId();
        if (sessionId) {
          dispatch(updateSessionFields({ sessionId, fields: { interviewee_consent_obtained: 'No' } }));
        }
        cleanupRecording(dispatch);
        if (sessionId && transcriptId) {
          sessionService.deleteTranscript(sessionId, transcriptId).catch(() => {
            dispatch(showError('Failed to delete consent-window transcript'));
          });
        } else if (sessionId) {
          // Transcript id not known yet (recording just started); erase it once it lands.
          pendingDeleteRef.current = true;
        }
        closeModal();
      },
    });
  }, [sessionId, dispatch, openModal, closeModal]);

  useEffect(() => {
    if (sessionId && capturedSessionRef.current !== sessionId) {
      capturedSessionRef.current = sessionId;
      initialConsentRef.current = consentStatus;
      declineHandledRef.current = false;
      finishPromptHandledRef.current = false;
      wasActivelyTranscribingRef.current = false;
    }
  }, [sessionId, consentStatus]);

  useEffect(() => {
    if (!enabled) return;
    const isActive = isRecording || isFileStreaming;

    if (sessionId) {
      if (consentStatus === 'declined' && initialConsentRef.current === 'pending' && !declineHandledRef.current) {
        declineHandledRef.current = true;
        openConsentModal();
      }
      if (wasActivelyTranscribingRef.current && !isActive && consentStatus === 'pending' && !finishPromptHandledRef.current) {
        finishPromptHandledRef.current = true;
        openConsentModal();
      }
    }

    wasActivelyTranscribingRef.current = isActive;
  }, [enabled, consentStatus, isRecording, isFileStreaming, sessionId, openConsentModal]);

  return { openConsentModal };
}
